// Keeps React from crashing when browser page translation (Chrome's built-in
// Google Translate, used by our Spanish-speaking workers) rewrites the DOM.
//
// Translation replaces text nodes with <font> wrappers. React still holds the
// original text nodes, so its next commit calls removeChild/insertBefore with
// a node that is no longer a child of that parent and the browser throws
// NotFoundError — taking the whole page down through the error boundary
// (e.g. Radix Select's SelectItemText in the pagination page-size picker).
//
// We don't disable translation (translate="no") because workers rely on it.
// Instead, operations on a node that has already been moved are skipped:
// the node is already gone from that parent, which is what React wanted.
// See https://github.com/facebook/react/issues/11538.

const GUARD_MARKER = Symbol.for("shyne.translateDomGuard");

type GuardableProto = Pick<Node, "removeChild" | "insertBefore"> & { [GUARD_MARKER]?: true };

export function installTranslateDomGuard(
  proto: GuardableProto | undefined = typeof Node === "function" ? Node.prototype : undefined
): void {
  if (!proto || proto[GUARD_MARKER]) return;
  proto[GUARD_MARKER] = true;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn("Skipped removeChild of a node moved by page translation", child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn("Skipped insertBefore with a reference node moved by page translation", referenceNode);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
